import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { SelectFieldComponent, SelectFieldOption } from '@evorait/pace-components';
import { DateUtilities, TimesheetSummary } from '@evorait/pace-core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import moment from 'moment';

const NO_BREAK_SPACE = '\u00A0';

/**
 * @param text the text to keep on one line
 * @returns the text with each space replaced by a no-break space
 */
function unbreakable(text: string): string {
    return text.replace(/ /g, NO_BREAK_SPACE);
}

/**
 * Picks which timesheet the page shows.
 *
 * Deliberately dumb: it takes the summaries and the resolved selection, and reports the user's
 * choice upward. It holds no store reference, so it can be rendered from a spec or a story with
 * plain data.
 *
 * Presented by `pace-select-field` rather than a native `<select>`, so the picker looks and behaves
 * like every other choice on the page. Its value is the timesheet id — a scalar, which is why this
 * uses the select field rather than `pace-dropdown-field` underneath it.
 */
@Component({
    selector: 'pace-timesheet-picker',
    standalone: true,
    imports: [SelectFieldComponent, TranslateModule],
    templateUrl: './timesheet-picker.component.html',
    styleUrl: './timesheet-picker.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimesheetPickerComponent {
    private readonly translate = inject(TranslateService);

    /** The selectable timesheets, newest first. */
    public readonly summaries = input.required<readonly TimesheetSummary[]>();

    /**
     * The timesheet currently shown — the *resolved* selection, not the raw slice field, so the
     * option the user sees marked is the one actually rendered.
     */
    public readonly selectedTimesheetId = input<string | undefined>(undefined);

    /** The timesheet the user picked. */
    public readonly selected = output<string>();

    /** One option per timesheet, in the order the summaries arrived. */
    protected readonly options = computed<SelectFieldOption[]>(() =>
        this.summaries().map((summary) => ({ value: summary.timesheetId, label: this.optionLabel(summary) })),
    );

    /** The field's value is a string, so "nothing resolved yet" is the empty one rather than absent. */
    protected readonly selection = computed(() => this.selectedTimesheetId() ?? '');

    /**
     * @param timesheetId the picked timesheet's id, empty when the choice was cleared
     */
    protected onSelectionChange(timesheetId: string): void {
        if (timesheetId) {
            this.selected.emit(timesheetId);
        }
    }

    /**
     * What one timesheet reads as: when its work started, when it ended once it has been sent, and
     * whether it has been sent.
     *
     * The end is only shown for a sent timesheet: an open one's end of work can still change.
     *
     * The menu wraps long labels (see the global `.dropdown-item` rule), and a wrap at any space
     * split a date from its time. Every part is therefore kept on one line, and the separators stay
     * with the part before them, so a line can only break after "–" or "·".
     *
     * @param summary the timesheet to label
     * @returns the option's label
     */
    private optionLabel(summary: TimesheetSummary): string {
        const started = unbreakable(
            summary.startOfWork
                ? this.formatStartOfWork(summary.startOfWork)
                : <string>this.translate.instant('TIMESHEET_PICKER_UNDATED_LABEL'),
        );
        const period =
            summary.isSubmitted && summary.startOfWork && summary.endOfWork
                ? `${started}${NO_BREAK_SPACE}– ${unbreakable(this.formatEndOfWork(summary.startOfWork, summary.endOfWork))}`
                : started;
        const status = unbreakable(
            <string>(
                this.translate.instant(
                    summary.isSubmitted ? 'TIMESHEET_PICKER_SUBMITTED_LABEL' : 'TIMESHEET_PICKER_OPEN_LABEL',
                )
            ),
        );

        return `${period}${NO_BREAK_SPACE}· ${status}`;
    }

    /**
     * Renders an instant the way the rows below render theirs.
     *
     * `DateUtilities` carries the locale the browser reported at startup, so the label follows the
     * technician's region — unlike the `date` pipe this used to use, which resolves against
     * Angular's `LOCALE_ID`. Nothing provides that, so it stays `en-US` for everyone and printed a
     * 12h time next to the 24h one the rows' native time inputs show.
     *
     * The time is part of the label because the date alone cannot tell two timesheets of the same
     * day apart.
     *
     * @param startOfWork the instant to render
     * @returns the instant in the browser's locale, in local time
     */
    private formatStartOfWork(startOfWork: string): string {
        return DateUtilities.formatDateTime(moment(startOfWork));
    }

    /**
     * Renders the end of work next to its start.
     *
     * Only the time when the work ended on the day it started, so the label stays short; the full
     * date and time when it ran past midnight, so a night shift does not read as ending before it
     * began.
     *
     * @param startOfWork the instant the work started
     * @param endOfWork the instant the work ended
     * @returns the end of work in the browser's locale, in local time
     */
    private formatEndOfWork(startOfWork: string, endOfWork: string): string {
        const end = moment(endOfWork);
        return end.isSame(moment(startOfWork), 'day')
            ? DateUtilities.formatTime(end)
            : DateUtilities.formatDateTime(end);
    }
}
